import { useRef } from 'react';
import { ToastManager } from '../../ToastManager';
import { userMeDraftStore } from '../../store/users/UserMeDraftStore.ts';

import { useI18n } from '../../hooks/useI18nFormatters';
import { useUserMe } from '../../store/users/useUserMe.ts';
import { useMood } from '../../store/moods/useMoods.ts';
import { useApi } from '../../store/SelectedHomeyStore.js';

import { theme } from '../../theme/theme';

import { ContextMenuContent } from '../common/context-menu/ContextMenuContent';
import { DeveloperMenu } from '../common/context-menu/DeveloperMenu';

import { iconMouseLeftClick } from '../../theme/icons/interface/mouse-left-click';
import { iconNotFavorite } from '../../theme/icons/interface/heart-outline';
import { iconFavorite } from '../../theme/icons/interface/heart-fill';
import { iconTrashFill } from '../../theme/icons/interface/trash-fill/trash-fill';

export function MoodContextMenuContent(props) {
  const { i18n } = useI18n();
  // const ctx = useContextMenuContext();

  const pendingRef = useRef({
    favorite: false,
  });
  const { api } = useApi();
  const { mood } = useMood({ moodId: props.moodId });
  const { userMe } = useUserMe();

  const isFavoriteMood = userMe?.properties.favoriteMoods?.includes(props.moodId) ?? false;

  async function onAction(key) {
    switch (key) {
      case 'set':
        api.moods
          .setMood({ id: props.moodId })
          .then(() => {})
          .catch(ToastManager.handleError);
        break;
      case 'favorite':
        (async () => {
          const favoriteName = `**${mood.name}**`;

          if (pendingRef.current.favorite === true) {
            ToastManager.add({
              message: 'Request still pending, please wait...',
            });
            return;
          }

          if (isFavoriteMood) {
            try {
              pendingRef.current.favorite = true;

              const nextUserMe = {
                ...userMe,
                properties: {
                  ...userMe.properties,
                  favoriteMoods: (userMe.properties.favoriteMoods ?? []).filter(
                    (id) => id !== props.moodId
                  ),
                },
              };
              userMeDraftStore.setUserMeDraft({ userMe: nextUserMe });

              await api.users.updateUserMeProperties({
                id: 'favoriteMoods',
                value: nextUserMe.properties.favoriteMoods,
              });

              ToastManager.add({
                icon: iconNotFavorite,
                iconColor: theme.icon.color_light,
                message: i18n.messageFormatter(`mood.removeFavorite`, {
                  name: favoriteName,
                }),
              });
              userMeDraftStore.setUserMeDraftDone();
            } catch (error) {
              userMeDraftStore.setUserMeDraftFailed();
              ToastManager.handleError(error);
            } finally {
              pendingRef.current.favorite = false;
            }

            return;
          }

          try {
            pendingRef.current.favorite = true;

            const nextUserMe = {
              ...userMe,
              properties: {
                ...userMe.properties,
                favoriteMoods: [...(userMe.properties.favoriteMoods ?? []), props.moodId],
              },
            };
            userMeDraftStore.setUserMeDraft({ userMe: nextUserMe });

            await api.users.updateUserMeProperties({
              id: 'favoriteMoods',
              value: nextUserMe.properties.favoriteMoods,
            });

            ToastManager.add({
              icon: iconFavorite,
              iconColor: theme.color.special_favorite,
              message: i18n.messageFormatter(`mood.addFavorite`, {
                name: favoriteName,
              }),
            });
            userMeDraftStore.setUserMeDraftDone();
          } catch (error) {
            userMeDraftStore.setUserMeDraftFailed();
            ToastManager.handleError(error);
          } finally {
            pendingRef.current.favorite = false;
          }
        })().catch(ToastManager.handleError);
        break;
      case 'delete':
        api.moods
          .deleteMood({ id: props.moodId })
          .then(() => {})
          .catch(ToastManager.handleError);
        break;
      default:
        break;
    }
  }

  return (
    <ContextMenuContent.List>
      <ContextMenuContent.ListItem
        label={i18n.messageFormatter(`mood.set`)}
        icon={iconMouseLeftClick}
        onPress={() => {
          onAction('set');
        }}
      />

      <ContextMenuContent.ListItem
        label={i18n.messageFormatter(`mood.favorite`)}
        icon={isFavoriteMood ? iconFavorite : iconNotFavorite}
        onPress={() => {
          onAction('favorite');
        }}
      />

      <DeveloperMenu onIdRequest={() => mood?.id} onResourceRequest={() => mood} />

      <ContextMenuContent.Divider />

      <ContextMenuContent.ListItem
        label={i18n.messageFormatter('common.delete')}
        variant="danger"
        icon={iconTrashFill}
        onPress={() => {
          onAction('delete');
        }}
      />
    </ContextMenuContent.List>
  );
}
